/**
 * Display conventions for the Entity Management module (FD-4464, spec §4.5 / AC 8).
 *
 * The currency formatter cannot be expressed as `Intl.NumberFormat` config, which
 * is why it is registered as a static formatter function rather than an entry in
 * `NumericFormattersConfig` (the multiple display *can* be, so it lives there as
 * `entityMultiple`):
 *   - the magnitude tiers use different precision (1dp for millions, 2dp for
 *     billions), and `Intl` takes a single `maximumFractionDigits`
 *   - the thousands suffix is lowercase `k`; `Intl` emits `K`
 *   - negatives use a typographic minus (U+2212); `Intl` emits an ASCII hyphen
 *
 * TODO(FD-4464): the tier rule is inferred from the 11 rows visible in the Tessera
 * prototype (1dp millions keeping trailing zeros — `$92.0M`, `$66.0M`; 2dp billions
 * — `$1.12B`; `$500k` the only thousands sample). Confirm with product, especially
 * the thousands tier.
 */

/** U+2212. The spec is explicit that this is not an ASCII hyphen. */
const MINUS_SIGN = '−';
/** U+2014. Used for missing values, which must stay distinguishable from $0. */
export const EM_DASH = '—';
/** Shown when a multiple is not meaningful (e.g. a carry vehicle with negative NAV). Consumed by the `entityMultiple` config entry in `NumericFormattersConfig`. */
export const NOT_MEANINGFUL = 'n/m';

const BILLION = 1_000_000_000;
const MILLION = 1_000_000;
const THOUSAND = 1_000;

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const num = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(num) ? num : null;
}

/**
 * Drops noise zeros from the fraction so the thousands tier reads `$500k` rather
 * than `$500.0k` (and, under an overridden precision, `$1.5k` rather than `$1.50k`).
 */
function trimTrailingZeros(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

/**
 * Per-tier decimal precision for {@link createCurrencyMagnitudeFormatter}.
 * Omitted tiers keep the module defaults (billions 2dp, millions 1dp, thousands 1dp).
 */
export interface CurrencyMagnitudeDecimals {
  billions?: number;
  millions?: number;
  thousands?: number;
}

interface Tier {
  unit: number;
  suffix: string;
  decimals: number;
  trimTrailing: boolean;
}

/**
 * Builds a magnitude-tiered currency formatter: `$314.2M`, `$1.12B`, `$500k`,
 * `−$38.6M` (typographic minus), `$0` distinct from an em dash for missing.
 *
 * Generic over per-tier decimal precision so variants can be derived without
 * duplicating the tiering rules — register a derived instance in
 * `StaticFormattersById` to expose it as a formatter id.
 */
export function createCurrencyMagnitudeFormatter(
  decimals: CurrencyMagnitudeDecimals = {}
): (value: unknown) => string {
  const tiers: Tier[] = [
    { unit: BILLION, suffix: 'B', decimals: decimals.billions ?? 2, trimTrailing: false },
    { unit: MILLION, suffix: 'M', decimals: decimals.millions ?? 1, trimTrailing: false },
    { unit: THOUSAND, suffix: 'k', decimals: decimals.thousands ?? 1, trimTrailing: true },
  ];

  function renderTier(abs: number, tierIndex: number): string {
    const { unit, suffix, decimals: tierDecimals, trimTrailing } = tiers[tierIndex];
    const scaled = (abs / unit).toFixed(tierDecimals);

    // Rounding can push a value just under a boundary into a four-digit mantissa —
    // 999,999 would render as `$1000k` rather than `$1.0M`, and 999,999,999 as
    // `$1000.0M` rather than `$1.00B`. Promote a tier when that happens.
    if (Number(scaled) >= 1000 && tierIndex > 0) {
      return renderTier(abs, tierIndex - 1);
    }

    return `${trimTrailing ? trimTrailingZeros(scaled) : scaled}${suffix}`;
  }

  /**
   * Tier is chosen by magnitude, then promoted only if rendering overflows.
   *
   * Choosing the tier from the *rounded* value instead would be too eager: anything from
   * $950k up rounds to `1.0` at one decimal, so $950,000 would render as `$1.0M` rather
   * than `$950k`, losing real precision across a wide band.
   */
  function formatMagnitude(abs: number): string {
    const tierIndex = tiers.findIndex(({ unit }) => abs >= unit);
    if (tierIndex !== -1) {
      return renderTier(abs, tierIndex);
    }

    // Sub-thousand, unless rounding lifts it into the thousands tier (999.6 -> $1k).
    const whole = abs.toFixed(0);

    return Number(whole) >= THOUSAND ? renderTier(abs, tiers.length - 1) : whole;
  }

  return function formatCurrencyMagnitude(value: unknown): string {
    const num = toFiniteNumber(value);
    if (num === null) {
      return EM_DASH;
    }
    if (num === 0) {
      return '$0';
    }

    const sign = num < 0 ? MINUS_SIGN : '';

    return `${sign}$${formatMagnitude(Math.abs(num))}`;
  };
}

/**
 * Module currency display (spec §4.5 defaults): `$314.2M`, `$1.12B`, `$500k`,
 * `−$38.6M`. `$0` is deliberately distinct from a missing value, which renders as
 * an em dash — a zero position and an unknown position mean different things here.
 */
export const formatEntityCurrency = createCurrencyMagnitudeFormatter();
